import { DEFAULT_STAGE_ID } from './board.js';
import type { BoardData, Frame, FrameKind, Item } from './schema.js';

export interface Point {
  x: number;
  y: number;
}

/** Position of an item in the 'Not placed' tray. */
export const IN_TRAY: { readonly frameId: null; readonly unplaced: true } = {
  frameId: null,
  unplaced: true,
};

/** Metres between areas placed automatically. */
export const AREA_GAP = 2;

export const DEFAULT_AREA_SIZE: Readonly<Record<FrameKind, { width: number; depth: number }>> = {
  stage: { width: 10, depth: 6 },
  foh: { width: 4, depth: 3 },
  monitor: { width: 3, depth: 3 },
  area: { width: 6, depth: 4 },
};

/** The main stage: the default stage frame, else the first stage by id, else the first frame. */
export function mainStage(data: Pick<BoardData, 'frames'>): Frame | undefined {
  const byId = (a: Frame, b: Frame): number => (a.id < b.id ? -1 : 1);
  const frames = Object.values(data.frames).sort(byId);
  const def = data.frames[DEFAULT_STAGE_ID];
  if (def !== undefined) return def;
  const stages = frames.filter((f) => f.kind === 'stage');
  return stages[0] ?? frames[0];
}

/** Origin the item's x/y are relative to: its area, the world (free), undefined (missing area). */
export function areaOrigin(
  data: Pick<BoardData, 'frames'>,
  item: Pick<Item, 'frameId'>,
): Point | undefined {
  if (item.frameId === null) return { x: 0, y: 0 };
  const f = data.frames[item.frameId];
  return f === undefined ? undefined : { x: f.x, y: f.y };
}

/** World position of an item; undefined in the tray or in a missing area. */
export function worldPosition(
  data: Pick<BoardData, 'frames' | 'items'>,
  item: Item,
): Point | undefined {
  if (item.unplaced === true) return undefined;
  const o = areaOrigin(data, item);
  return o === undefined ? undefined : { x: o.x + item.x, y: o.y + item.y };
}

/** Area containing the world point (edges inclusive); the last match wins. */
export function areaAt(data: Pick<BoardData, 'frames'>, p: Point): Frame | undefined {
  let hit: Frame | undefined;
  for (const f of Object.values(data.frames)) {
    if (p.x >= f.x && p.x <= f.x + f.width && p.y >= f.y && p.y <= f.y + f.depth) hit = f;
  }
  return hit;
}

const round = (n: number): number => Math.round(n * 1e6) / 1e6;

/** Frame and local coordinates for a world point: inside an area, else free in the world. */
export function placeAt(
  data: Pick<BoardData, 'frames'>,
  p: Point,
): { frameId: string | null; x: number; y: number } {
  const f = areaAt(data, p);
  return f === undefined
    ? { frameId: null, x: round(p.x), y: round(p.y) }
    : { frameId: f.id, x: round(p.x - f.x), y: round(p.y - f.y) };
}

type Rect = Pick<Frame, 'x' | 'y' | 'width' | 'depth'>;

/** Whether two areas overlap; touching edges do not. */
export function areasOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.depth && b.y < a.y + a.depth;
}

/** Shortest move (metres) that is still carried out; a clamped move below it is rejected. */
const MIN_MOVE = 0.01;
/** Smallest edge length of an area (metres). */
export const MIN_AREA_EDGE = 0.5;

/** Rounds and turns -0 into 0. */
const clean = (n: number): number => round(n) + 0;

/**
 * Clamps a move of area `id` by (dx, dy) so it does not overlap another area (touching is fine):
 * the largest fraction of the delta without overlap, or {0, 0} when that is under 1 cm.
 */
export function clampAreaDelta(
  frames: readonly Frame[],
  id: string,
  dx: number,
  dy: number,
): { dx: number; dy: number } {
  const f = frames.find((x) => x.id === id);
  if (f === undefined) return { dx: 0, dy: 0 };
  const others = frames.filter((x) => x.id !== id);
  const clashes = (t: number, d = { dx: dx * t, dy: dy * t }): boolean =>
    others.some((o) => areasOverlap({ ...f, x: f.x + d.dx, y: f.y + d.dy }, o));
  if (!clashes(1)) return { dx, dy };
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (clashes(mid)) hi = mid;
    else lo = mid;
  }
  let out = { dx: clean(dx * lo), dy: clean(dy * lo) };
  if (clashes(out.dx === 0 && out.dy === 0 ? 0 : 1, out)) {
    // rounding crossed the boundary by under 1e-6: step back towards zero
    const back = (n: number): number => clean(n - Math.sign(n) * 1e-6);
    out = { dx: back(out.dx), dy: back(out.dy) };
  }
  return Math.hypot(out.dx, out.dy) < MIN_MOVE ? { dx: 0, dy: 0 } : out;
}

/**
 * Clamps the new size of area `id` (origin stays) so it overlaps no other area. Two overlap-free
 * candidates: width first (limited by areas to the right within the current depth band), then
 * depth; or depth first (areas below within the current width band), then width. The one with the
 * larger area wins (tie: width first). Minimum 0.5 m.
 */
export function clampAreaSize(
  frames: readonly Frame[],
  id: string,
  width: number,
  depth: number,
): { width: number; depth: number } {
  const f = frames.find((x) => x.id === id);
  const w0 = Math.max(MIN_AREA_EDGE, width);
  const d0 = Math.max(MIN_AREA_EDGE, depth);
  if (f === undefined) return { width: w0, depth: d0 };
  const others = frames.filter((x) => x.id !== id);
  const limitWidth = (w: number, d: number): number =>
    Math.max(
      MIN_AREA_EDGE,
      others.reduce(
        (m, o) => (o.x >= f.x && o.y < f.y + d && f.y < o.y + o.depth ? Math.min(m, o.x - f.x) : m),
        w,
      ),
    );
  const limitDepth = (d: number, w: number): number =>
    Math.max(
      MIN_AREA_EDGE,
      others.reduce(
        (m, o) => (o.y >= f.y && o.x < f.x + w && f.x < o.x + o.width ? Math.min(m, o.y - f.y) : m),
        d,
      ),
    );
  const aw = limitWidth(w0, f.depth);
  const a = { width: aw, depth: limitDepth(d0, aw) };
  const bd = limitDepth(d0, f.width);
  const b = { width: limitWidth(w0, bd), depth: bd };
  return b.width * b.depth > a.width * a.depth ? b : a;
}

/** Spot for a new area: right of all areas with {@link AREA_GAP}, aligned to the main stage. */
export function newAreaSpot(
  frames: readonly Frame[],
  _size: { width: number; depth: number },
): Point {
  if (frames.length === 0) return { x: 0, y: 0 };
  const right = Math.max(...frames.map((f) => f.x + f.width));
  const y = mainStage({ frames: Object.fromEntries(frames.map((f) => [f.id, f])) })!.y;
  return { x: right + AREA_GAP, y };
}

/** English default name for a new area of `kind`, numbered by the frames of that kind. */
export function defaultAreaName(kind: FrameKind, frames: readonly Frame[]): string {
  const n = frames.filter((f) => f.kind === kind).length + 1;
  const base = { stage: 'Stage', foh: 'FOH', monitor: 'Monitor world', area: 'Area' }[kind];
  return n === 1 && kind !== 'area' ? base : `${base} ${n}`;
}

/** v4 → v5: the items of the old tray (`frameId: null`) as unplaced copies. */
export function trayItemChanges(items: Record<string, Item>): Item[] {
  return Object.values(items)
    .filter((i) => i.frameId === null && i.unplaced !== true)
    .map((i) => ({ ...i, ...IN_TRAY }));
}
